const includesScopeLevel = (scopeLevel, requiredScopeLevel) => {
    switch (requiredScopeLevel) {
        case 'write':
            return scopeLevel === 'write' || scopeLevel === 'admin';
        case 'read':
            return (scopeLevel === 'read' ||
                scopeLevel === 'write' ||
                scopeLevel === 'admin');
        case 'admin':
            return scopeLevel === 'admin';
    }
};
const calculateRequiredScopeLevel = (requiredCapabilities) => {
    return requiredCapabilities.reduce((acc, capability) => {
        if (acc === 'admin') {
            return 'admin';
        }
        if (capability.endsWith('-create') ||
            capability.endsWith('-update') ||
            capability.endsWith('-delete')) {
            return 'write';
        }
        else if (!capability.endsWith('-read')) {
            return 'admin';
        }
        return acc;
    }, 'read');
};
export const calculateRequiredScope = ({ resource, requiredCapabilities, }) => ({
    scopeLevel: calculateRequiredScopeLevel(requiredCapabilities),
    resource,
});
export const scopeMatches = ({ scopes, requiredScope, }) => Array.isArray(scopes) &&
    !!scopes.find((s) => (!s.scopeLevel ||
        !requiredScope.scopeLevel ||
        includesScopeLevel(s.scopeLevel, requiredScope.scopeLevel)) &&
        (!s.resource.id ||
            s.resource.id === requiredScope.resource.id ||
            s.resource.id === '*' ||
            requiredScope.resource.id === '*') &&
        (s.resource.type === requiredScope.resource.type ||
            s.resource.type === '*' ||
            requiredScope.resource.type === '*'));
export const getPublicPostReadScope = (postId) => ({
    scopeLevel: 'read',
    resource: { type: 'posts', id: postId },
});
/**
 * Posts with `public` visibility — and posts in groups that grant the needed
 * *-read caps to `none` — are readable without auth; grant read scope for
 * that post id (same pattern as {@link withPublicGroupReadScopes}).
 */
export const withPublicPostReadScopes = (authData, post, neededCapabilities) => {
    var _a;
    if (!neededCapabilities.every((c) => c.endsWith('-read'))) {
        return authData;
    }
    const publicGroupPost = post.visibility === 'group' &&
        neededCapabilities.every((c) => { var _a, _b, _c, _d, _e; return (_e = (_d = (_c = (_b = (_a = post.group) === null || _a === void 0 ? void 0 : _a.capabilities) === null || _b === void 0 ? void 0 : _b.none) === null || _c === void 0 ? void 0 : _c.add) === null || _d === void 0 ? void 0 : _d.includes(c)) !== null && _e !== void 0 ? _e : false; });
    if (post.visibility !== 'public' && !publicGroupPost) {
        return authData;
    }
    const publicReadScope = getPublicPostReadScope(post.id);
    const scopes = (_a = authData.scopes) !== null && _a !== void 0 ? _a : [];
    if (scopeMatches({ scopes, requiredScope: publicReadScope })) {
        return authData;
    }
    return { ...authData, scopes: [...scopes, publicReadScope] };
};
export const getPublicGroupReadScope = (groupId) => ({
    scopeLevel: 'read',
    resource: { type: 'groups', id: groupId },
});
/**
 * Groups that grant the needed *-read caps to relationship `none` are readable
 * without auth (same pattern as {@link withPublicPostReadScopes}).
 */
export const withPublicGroupReadScopes = (authData, group, neededCapabilities) => {
    var _a, _b, _c, _d;
    if (!neededCapabilities.every((c) => c.endsWith('-read'))) {
        return authData;
    }
    const noneAdd = (_c = (_b = (_a = group.capabilities) === null || _a === void 0 ? void 0 : _a.none) === null || _b === void 0 ? void 0 : _b.add) !== null && _c !== void 0 ? _c : [];
    if (!neededCapabilities.every((c) => noneAdd.includes(c))) {
        return authData;
    }
    const publicReadScope = getPublicGroupReadScope(group.id);
    const scopes = (_d = authData.scopes) !== null && _d !== void 0 ? _d : [];
    if (scopeMatches({ scopes, requiredScope: publicReadScope })) {
        return authData;
    }
    return { ...authData, scopes: [...scopes, publicReadScope] };
};
export const getPublicProfileReadScope = (profileId) => ({
    scopeLevel: 'read',
    resource: { type: 'profiles', id: profileId },
});
/**
 * Profiles whose capabilities config grants the needed *-read caps to
 * relationship `none` are readable without auth (same pattern as
 * {@link withPublicGroupReadScopes}). Community-wide `publicContent` is
 * still enforced by `ensureAccess` on the profile endpoints.
 */
export const withPublicProfileReadScopes = (authData, profile, neededCapabilities, config) => {
    var _a, _b, _c, _d;
    if (!neededCapabilities.every((c) => c.endsWith('-read'))) {
        return authData;
    }
    const noneAdd = (_c = (_b = (_a = config.profile) === null || _a === void 0 ? void 0 : _a.none) === null || _b === void 0 ? void 0 : _b.add) !== null && _c !== void 0 ? _c : [];
    if (!neededCapabilities.every((c) => noneAdd.includes(c))) {
        return authData;
    }
    const publicReadScope = getPublicProfileReadScope(profile.id);
    const scopes = (_d = authData.scopes) !== null && _d !== void 0 ? _d : [];
    if (scopeMatches({ scopes, requiredScope: publicReadScope })) {
        return authData;
    }
    return { ...authData, scopes: [...scopes, publicReadScope] };
};
